import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { claims as claimsApi, hrms } from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { theme } from '../../theme';

const CLAIM_CATEGORIES = ['Travel', 'Accommodation', 'Meals', 'Equipment', 'Software', 'Training', 'Medical', 'Office Supplies', 'Communication', 'Other'];

function ClaimItem({ item, index, onChange, onRemove }) {
  const inp = { width: '100%', padding: '0.5rem 0.75rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.8rem', color: theme.text, outline: 'none', boxSizing: 'border-box' };
  const billAvail = item.bill_available !== false;

  const toggleStyle = (active, color) => ({
    padding: '0.35rem 0.9rem', borderRadius: '0.375rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', border: 'none',
    background: active ? color : theme.border, color: active ? '#fff' : theme.textMuted, transition: 'all 0.15s',
  });

  return (
    <div style={{ background: theme.pageBg, borderRadius: '0.5rem', padding: '1rem', border: `1px solid ${theme.border}`, marginBottom: '0.75rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <p style={{ fontWeight: 600, fontSize: '0.875rem', color: theme.text, margin: 0 }}>Item #{index + 1}</p>
        <button onClick={() => onRemove(index)} style={{ background: 'none', border: 'none', color: theme.danger, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Remove</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: theme.text, marginBottom: '0.3rem' }}>Description *</label>
          <input style={inp} value={item.description} onChange={(e) => onChange(index, 'description', e.target.value)} placeholder="Item description" />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: theme.text, marginBottom: '0.3rem' }}>Amount (₹) *</label>
          <input style={inp} type="number" value={item.amount} onChange={(e) => onChange(index, 'amount', e.target.value)} placeholder="0.00" />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: theme.text, marginBottom: '0.3rem' }}>Expense Date *</label>
          <input style={inp} type="date" value={item.expense_date} onChange={(e) => onChange(index, 'expense_date', e.target.value)} max={new Date().toISOString().split('T')[0]} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: theme.text, marginBottom: '0.3rem' }}>Category</label>
          <select style={inp} value={item.category} onChange={(e) => onChange(index, 'category', e.target.value)}>
            {CLAIM_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: theme.text, marginBottom: '0.4rem' }}>Bill Available? *</label>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <button type="button" style={toggleStyle(billAvail, theme.success)} onClick={() => onChange(index, 'bill_available', true)}>Yes</button>
            <button type="button" style={toggleStyle(!billAvail, theme.danger)} onClick={() => { onChange(index, 'bill_available', false); onChange(index, 'file', null); }}>No</button>
          </div>
          {billAvail && (
            <div>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: theme.text, marginBottom: '0.3rem' }}>
                Receipt / File <span style={{ color: theme.danger }}>*</span>
              </label>
              {item.existing_bill_path && !item.file && (
                <p style={{ fontSize: '0.72rem', color: theme.success, marginBottom: '0.25rem', margin: '0 0 0.25rem' }}>✓ Existing receipt on file (upload new to replace)</p>
              )}
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ fontSize: '0.75rem', width: '100%' }} onChange={(e) => onChange(index, 'file', e.target.files[0])} />
            </div>
          )}
          {!billAvail && (
            <p style={{ fontSize: '0.75rem', color: theme.textMuted, fontStyle: 'italic', margin: 0 }}>No bill — receipt upload not required.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function ClaimForm() {
  const navigate = useNavigate();
  const { id: editId } = useParams();          // present on /employee/claims/:id/edit
  const isEdit = Boolean(editId);
  const [isDraft, setIsDraft] = useState(false);
  const [draftSaving, setDraftSaving] = useState(false);

  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ project_id: '', title: '', description: '' });
  const [items, setItems] = useState([{ description: '', amount: '', category: 'Travel', expense_date: '', file: null, bill_available: true }]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEdit);
  const [error, setError] = useState('');

  useEffect(() => {
    hrms.getProjects().then((r) => setProjects(r.data || [])).catch(err => console.warn('[HRMS]', err?.message));
  }, []);

  // Load existing claim for edit mode
  useEffect(() => {
    if (!isEdit) return;
    setFetchLoading(true);
    claimsApi.getClaim(editId).then((r) => {
      const c = r.data;
      if (c.status === 'draft') setIsDraft(true);
      if (c.status !== 'submitted' && c.status !== 'under_review' && c.status !== 'draft') {
        alert('This claim can no longer be edited.');
        navigate('/employee/claims');
        return;
      }
      setForm({ project_id: c.project_id || '', title: c.title || '', description: c.description || '' });
      if (c.items?.length) {
        setItems(c.items.map((it) => ({
          description: it.description || '',
          amount: it.amount || '',
          category: it.category || 'Travel',
          expense_date: it.expense_date ? it.expense_date.split('T')[0] : '',
          file: null,
          bill_available: it.bill_available !== false,
          existing_bill_path: it.bill_file_path || null,
          existing_bill_type: it.bill_file_type || null,
        })));
      }
    }).catch(() => {
      alert('Failed to load claim.');
      navigate('/employee/claims');
    }).finally(() => setFetchLoading(false));
  }, [editId, isEdit, navigate]);

  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addItem = () => {
    setItems((prev) => [...prev, { description: '', amount: '', category: 'Travel', expense_date: '', file: null, bill_available: true }]);
  };

  const removeItem = (index) => {
    if (items.length === 1) return;
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const totalAmount = items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);

  const buildFormData = () => {
    const fd = new FormData();
    if (form.project_id) fd.append('project_id', form.project_id);
    fd.append('title', form.title || 'Draft');
    if (form.description) fd.append('description', form.description);
    fd.append('total_amount', totalAmount);
    fd.append('items_json', JSON.stringify(items.map((item) => ({
      description: item.description,
      amount: Number(item.amount) || 0,
      category: item.category,
      expense_date: item.expense_date,
      bill_available: item.bill_available !== false,
      existing_bill_path: item.existing_bill_path || null,
      existing_bill_type: item.existing_bill_type || null,
    }))));
    items.forEach((item, i) => { if (item.file) fd.append(`item_file_${i}`, item.file); });
    return fd;
  };

  const handleSaveDraft = async () => {
    if (!form.title) { setError('Please enter a claim title before saving draft.'); return; }
    setDraftSaving(true); setError('');
    try {
      const fd = buildFormData();
      if (isDraft && editId) fd.append('draft_id', editId);
      const res = await claimsApi.saveDraft(fd);
      setIsDraft(true);
      navigate(`/employee/claims/${res.data.id}/edit`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to save draft.');
    } finally { setDraftSaving(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) { setError('Claim title is required.'); return; }
    if (items.some((item) => !item.description || !item.amount || !item.expense_date)) {
      setError('All items must have a description, amount, and expense date.');
      return;
    }
    const missingReceipt = items.findIndex((item) => item.bill_available !== false && !item.file && !item.existing_bill_path);
    if (missingReceipt !== -1) {
      setError(`Item #${missingReceipt + 1}: Receipt is required when bill is available. Please upload a file or select "No" for bill available.`);
      return;
    }
    setLoading(true); setError('');
    try {
      if (isDraft && editId) {
        // First save the draft with latest data, then submit it
        const fd = buildFormData();
        fd.append('draft_id', editId);
        await claimsApi.saveDraft(fd);
        await claimsApi.submitDraft(editId);
      } else if (isEdit) {
        await claimsApi.editClaim(editId, buildFormData());
      } else {
        await claimsApi.submitClaim(buildFormData());
      }
      navigate('/employee/claims');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : `Failed to ${isEdit ? 'update' : 'submit'} claim. Please try again.`);
    } finally { setLoading(false); }
  };

  const inp = { width: '100%', padding: '0.625rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, background: theme.white, outline: 'none', boxSizing: 'border-box' };
  const lbl = { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: theme.text, marginBottom: '0.4rem' };

  if (fetchLoading) return <div style={{ padding: '3rem', textAlign: 'center', color: '#6B7280' }}>Loading claim…</div>;

  return (
    <div style={{ maxWidth: '720px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
        <Button variant="outlineGray" onClick={() => navigate('/employee/claims')}>← Back</Button>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>
          {isDraft ? 'Continue Draft Claim' : isEdit ? 'Edit Claim' : 'Submit New Claim'}
        </h1>
          <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
            {isDraft ? 'Complete your draft and submit when ready' : isEdit ? 'Update the details and resubmit' : 'Fill in the details for your expense claim'}
          </p>
        </div>
      </div>

      {error && (
        <div style={{ background: '#FEF2F2', border: `1px solid #FECACA`, borderRadius: '0.5rem', padding: '0.75rem 1rem', marginBottom: '1.5rem', color: theme.danger, fontSize: '0.875rem' }}>
          ⚠️ {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Claim Details */}
        <Card title="Claim Details" style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={lbl}>Project (optional)</label>
              <select style={inp} value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })}>
                <option value="">No specific project</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Claim Title *</label>
              <input style={inp} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Business Trip to Mumbai - March 2025" required />
            </div>
            <div>
              <label style={lbl}>Description</label>
              <textarea style={{ ...inp, resize: 'vertical', minHeight: '80px' }} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Brief description of the expenses..." />
            </div>
          </div>
        </Card>

        {/* Expense Items */}
        <Card title={`Expense Items (${items.length})`} titleAction={
          <Button type="button" size="sm" variant="outline" onClick={addItem}>+ Add Item</Button>
        } style={{ marginBottom: '1.5rem' }}>
          {items.map((item, i) => (
            <ClaimItem key={i} item={item} index={i} onChange={handleItemChange} onRemove={removeItem} />
          ))}

          {/* Total */}
          <div style={{ background: theme.lightBg, borderRadius: '0.5rem', padding: '1rem', border: `1px solid ${theme.primaryLight}`, marginTop: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, color: theme.text }}>Total Amount</span>
            <span style={{ fontSize: '1.25rem', fontWeight: 700, color: theme.primary }}>₹{totalAmount.toLocaleString()}</span>
          </div>
        </Card>

        {isDraft && (
          <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: '0.5rem', padding: '0.6rem 1rem', marginBottom: '1rem', fontSize: '0.8rem', color: '#92400E' }}>
            📝 This claim is saved as a draft. Complete it and click "Submit Claim" when ready.
          </div>
        )}
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <Button type="button" variant="outlineGray" onClick={() => navigate('/employee/claims')}>Cancel</Button>
          <Button type="button" variant="outline" loading={draftSaving} onClick={handleSaveDraft}>💾 Save as Draft</Button>
          <Button type="submit" variant="primary" loading={loading}>
            {isDraft ? 'Submit Claim' : isEdit ? 'Update Claim' : 'Submit Claim'}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default ClaimForm;
